import React, { useState } from 'react';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName } from '@/lib/slices/modal-slice';

import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import { CancelIcon, DeleteIcon } from '@/assets/icons/button';
import LoadingSpinner from '../../LoadingSpinner';

interface ConfirmationModalProps {
  modalName: string;
  title: string;
  subtitle?: string;
  message: string;
  description?: string;
  confirmText?: string;
  confirmIcon?: React.ReactNode;
  isDestructive?: boolean;
  onConfirm: () => Promise<void>;
  className?: string;
  closeOnClickOff?: boolean;
  customFooter?: React.ReactNode;
  secondaryAction?: {
    text: string;
    onClick: () => void;
  };
  skipAutoClose?: boolean;
}

export default function ConfirmationModal({
  modalName,
  title,
  subtitle,
  message,
  description,
  confirmText = 'Confirm',
  confirmIcon,
  isDestructive = false,
  onConfirm,
  className = 'max-w-[500px] w-full',
  closeOnClickOff = false,
  customFooter,
  secondaryAction,
  skipAutoClose = false,
}: ConfirmationModalProps) {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const currentModalName: string = useAppSelector(() => store.getState().modal.name);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleClose = () => {
    if (isLoading) return;
    dispatch(setModalName(''));
  };

  const handleConfirm = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsLoading(true);
    try {
      await onConfirm();
      if (!skipAutoClose) {
        handleClose();
      }
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isOpen = modalVisible && currentModalName === modalName;

  return (
    <ModalComponent
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel={title}
      contentSubtitle={subtitle}
      className={className}
      closeOnClickOff={closeOnClickOff}
      footer={
        customFooter || (
          <div className='w-full pt-4 flex flex-col gap-2'>
            <div className='flex flex-row gap-8 py-2'>
              <Button onClick={handleConfirm} disabled={isLoading} delete={isDestructive}>
                {confirmIcon && <span className='text-inherit mr-2'>{confirmIcon}</span>}
                {!confirmIcon && isDestructive && <DeleteIcon className='w-[20px] h-[20px] mr-2' />}
                {confirmText}
              </Button>
              {secondaryAction ? (
                <Button outline={true} onClick={secondaryAction.onClick} disabled={isLoading}>
                  {secondaryAction.text}
                </Button>
              ) : (
                <Button outline={true} onClick={handleClose} disabled={isLoading}>
                  <CancelIcon className='w-[20px] h-[20px] mr-2' /> Cancel
                </Button>
              )}
            </div>
          </div>
        )
      }
    >
      <div className='flex flex-col gap-[16px] w-full'>
        <div className='flex flex-col gap-2 bg-surface_gray_200 rounded-lg p-4 border border-border_gray_300'>
          <p className='text-base'>{message}</p>
          {description && <p className='text-sm text-text_gray_900'>{description}</p>}
        </div>
      </div>

      {isLoading && (
        <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
          <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
          <div className='flex flex-col items-center justify-center gap-2 text-sm z-20'>
            <LoadingSpinner />
            <div>Please wait...</div>
          </div>
        </div>
      )}
    </ModalComponent>
  );
}
